/**
 * Product Grid Swiper Functionality
 * Handles SwiperJS initialization for product grid sections
 */

// Prevent duplicate class declaration if script is loaded multiple times
if (typeof window.SuniceProductGridSwiper === 'undefined') {
  var SuniceProductGridSwiper = class {
  constructor() {
    this.swiperInstances = new Map();
  }

  initializeProductGridSwiper(swiperContainer) {
    if (!swiperContainer) {
      return;
    }
    
    // Lazy initialization: only initialize if visible or about to be visible
    if (!swiperContainer.dataset.initStarted) {
      swiperContainer.dataset.initStarted = 'true';
      
      // Check if already visible
      const rect = swiperContainer.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200;
      
      if (isVisible) {
        // Initialize immediately if visible
        this._doInitialize(swiperContainer);
      } else {
        // Use IntersectionObserver for lazy loading
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._doInitialize(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: '200px' });
        
        observer.observe(swiperContainer);
      }
      return;
    }
    
    // If already started, proceed with initialization
    this._doInitialize(swiperContainer);
  }
  
  _doInitialize(swiperContainer) {
    if (!swiperContainer) {
      return;
    }
    
    // Ensure container has a unique ID - this is critical for multiple sections
    if (!swiperContainer.id) {
      swiperContainer.id = `product-grid-swiper-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    const containerId = swiperContainer.id;
    
    // Check if this swiper is already initialized
    if (this.swiperInstances.has(containerId)) {
      // Destroy existing swiper and reinitialize to handle DOM updates
      const existingSwiper = this.swiperInstances.get(containerId);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
      }
      this.swiperInstances.delete(containerId);
    }
    
    // Function to initialize the swiper (called after slides are confirmed)
    const initializeSwiper = () => {
      // Count slides to determine if loop is possible
      const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
      if (slideCount === 0) {
        return;
      }
      
      // Flag to prevent infinite loops
      let isUpdating = false;
      
      // Get settings from the swiper container's data attributes
      const showArrows = swiperContainer.dataset.showArrows !== 'false';
      const autoplay = swiperContainer.dataset.autoplay === 'true';
      const autoplaySpeed = swiperContainer.dataset.autoplaySpeed || 5000;
      const infinite = swiperContainer.dataset.infinite !== 'false';
      
      // Get CSS variables for spacing and column count
      const computedStyle = getComputedStyle(swiperContainer);
      let columnCount = parseInt(computedStyle.getPropertyValue('--product-grid-column-count')) || 4;
      let mobileColumnCount = parseInt(computedStyle.getPropertyValue('--product-grid-mobile-column-count')) || 1;
      
      // Fallback: try reading from inline style if CSS variable isn't set
      if (!columnCount || columnCount === 0 || isNaN(columnCount)) {
        const inlineStyle = swiperContainer.getAttribute('style') || '';
        const columnMatch = inlineStyle.match(/--product-grid-column-count:\s*(\d+)/);
        if (columnMatch) {
          columnCount = parseInt(columnMatch[1]);
        }
        // Final fallback
        if (!columnCount || columnCount === 0 || isNaN(columnCount)) {
          columnCount = 4;
        }
      }
      
      if (!mobileColumnCount || mobileColumnCount === 0 || isNaN(mobileColumnCount)) {
        mobileColumnCount = 1;
      }
      
      // Find the navigation buttons in the parent wrapper
      // Use closest to find the wrapper that contains this swiper and its buttons
      const wrapper = swiperContainer.closest('.swiper-container-wrapper');
      const nextButton = wrapper ? wrapper.querySelector('.product-grid-nav-next') : null;
      const prevButton = wrapper ? wrapper.querySelector('.product-grid-nav-prev') : null;
      
      // If not found via wrapper, try using unique IDs as fallback
      if (!nextButton || !prevButton) {
        const containerId = swiperContainer.id;
        if (containerId) {
          const sectionIdMatch = containerId.match(/product-grid-swiper-(.+)/);
          const sectionId = sectionIdMatch ? sectionIdMatch[1] : null;
          if (sectionId) {
            const nextById = document.getElementById(`product-grid-next-${sectionId}`);
            const prevById = document.getElementById(`product-grid-prev-${sectionId}`);
            if (nextById && !nextButton) nextButton = nextById;
            if (prevById && !prevButton) prevButton = prevById;
          }
        }
      }
      
      // Count slides to determine if loop is possible
      const canLoop = infinite && slideCount >= 3;

    // Optimized function to update swiper params when viewport changes
    let lastViewportWidth = window.innerWidth;
    const updateSwiperParams = (swiperInstance) => {
      // Prevent infinite loops
      if (isUpdating) return;
      
      const viewportWidth = window.innerWidth;
      // Skip if width hasn't changed significantly (saves CPU on mobile)
      if (Math.abs(viewportWidth - lastViewportWidth) < 50) return;
      lastViewportWidth = viewportWidth;
      
      isUpdating = true;
      
      let currentColumnCount = mobileColumnCount;
      if (viewportWidth >= 1024) {
        currentColumnCount = columnCount;
      } else if (viewportWidth >= 768) {
        currentColumnCount = 2;
      }

      // Update swiper params to match current column count
      if (swiperInstance && swiperInstance.params) {
        swiperInstance.params.slidesPerView = currentColumnCount;
        swiperInstance.params.slidesPerGroup = 1; // Always move one slide at a time
        
        // Update breakpoint params
        if (swiperInstance.params.breakpoints) {
          if (viewportWidth >= 1024) {
            swiperInstance.params.breakpoints[1024].slidesPerView = columnCount;
            swiperInstance.params.breakpoints[1024].slidesPerGroup = 1;
          } else if (viewportWidth >= 768) {
            swiperInstance.params.breakpoints[768].slidesPerView = 2;
            swiperInstance.params.breakpoints[768].slidesPerGroup = 1;
          }
        }
        
        // Update swiper to apply changes
        if (swiperInstance.update) {
          swiperInstance.update();
        }
      }
      
      isUpdating = false;
    };
    
    // Force buttons to be visible
    if (nextButton) {
      nextButton.style.display = 'flex';
      nextButton.style.visibility = 'visible';
      nextButton.style.opacity = '1';
      nextButton.classList.remove('button-lock');
    }
    
    if (prevButton) {
      prevButton.style.display = 'flex';
      prevButton.style.visibility = 'visible';
      prevButton.style.opacity = '1';
      prevButton.classList.remove('button-lock');
    }
    
    // Determine initial column count for config
    const viewportWidth = window.innerWidth;
    let initialColumnCount = mobileColumnCount;
    if (viewportWidth >= 1024) {
      initialColumnCount = columnCount;
    } else if (viewportWidth >= 768) {
      initialColumnCount = 2;
    }

    // Product Grid specific swiper configuration with enhanced mobile settings
    const swiperConfig = {
      loop: canLoop,
      slidesPerView: initialColumnCount, // Use actual number instead of 'auto' for better control
      spaceBetween: 16,
      slidesPerGroup: 1, // Move one slide at a time
      autoHeight: true, // CRITICAL: Auto-adjust height to content, prevents full-screen height bug
      navigation: nextButton && prevButton ? {
        nextEl: nextButton,
        prevEl: prevButton,
      } : false,
      autoplay: autoplay ? {
        delay: parseInt(autoplaySpeed),
        disableOnInteraction: false,
        pauseOnMouseEnter: true, // Pause on hover for product grids
      } : false,
      allowTouchMove: true,
      grabCursor: true,
      // Product grid specific settings
      nested: true,
      watchSlidesProgress: false,
      watchSlidesVisibility: false,
      // Enhanced touch support for product grids
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      // Better performance for product grids
      preloadImages: false,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2,
      },
      // Product grid specific mobile behavior
      centeredSlides: false,
      freeMode: false,
      // Update swiper params after initialization
      on: {
        init: function() {
          // Update params after initialization
          setTimeout(() => {
            updateSwiperParams(this);
          }, 100);
        },
        resize: function() {
          // Update params on resize
          updateSwiperParams(this);
        }
      },
      breakpoints: {
        // Mobile-first approach with specific product grid settings
        320: {
          slidesPerView: mobileColumnCount,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        480: {
          slidesPerView: mobileColumnCount,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        1024: {
          slidesPerView: columnCount,
          spaceBetween: 16,
          slidesPerGroup: 1,
        }
      }
    };

    // Initialize product grid swiper
    try {
      // Ensure section is visible before initializing
      const section = swiperContainer.closest('section, .section, [data-section]');
      const ensureVisible = () => {
        return new Promise((resolve) => {
          if (!section) {
            resolve();
            return;
          }
          
          const checkVisibility = () => {
            const rect = section.getBoundingClientRect();
            const style = window.getComputedStyle(section);
            const isVisible = rect.width > 0 && rect.height > 0 && 
                             style.visibility !== 'hidden' && 
                             style.display !== 'none' &&
                             style.opacity !== '0';
            
            if (isVisible) {
              resolve();
            } else {
              requestAnimationFrame(checkVisibility);
            }
          };
          
          checkVisibility();
          // Fallback timeout
          setTimeout(resolve, 500);
        });
      };
      
      // Wait for section to be visible, then initialize
      ensureVisible().then(() => {
        // Verify swiperContainer is still valid before proceeding
        if (!swiperContainer || !(swiperContainer instanceof Element) || !swiperContainer.isConnected) {
          return;
        }
        
        // FORCE reset all heights BEFORE initializing swiper
        swiperContainer.style.height = 'auto';
        swiperContainer.style.minHeight = '0';
        swiperContainer.style.maxHeight = 'none';
        
        const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
        if (swiperWrapper) {
          swiperWrapper.style.height = 'auto';
          swiperWrapper.style.minHeight = '0';
          swiperWrapper.style.maxHeight = 'none';
        }
        
        const slides = swiperContainer.querySelectorAll('.swiper-slide');
        slides.forEach(slide => {
          slide.style.height = 'auto';
          slide.style.minHeight = '0';
          slide.style.maxHeight = 'none';
        });
        
        const globalWindow = /** @type {Record<string, any>} */ (window);
        const SwiperConstructor = globalWindow.Swiper;
        
        if (typeof SwiperConstructor !== 'function') {
          return;
        }
        
        const swiper = new SwiperConstructor(swiperContainer, swiperConfig);
        this.swiperInstances.set(containerId, swiper);
      
        // Force update immediately and after images load
        const fixHeights = () => {
          if (swiper && swiper.update) {
            // Force all heights to auto
            swiperContainer.style.height = 'auto';
            swiperContainer.style.minHeight = '0';
            swiperContainer.style.maxHeight = 'none';
            
            if (swiperWrapper) {
              swiperWrapper.style.height = 'auto';
              swiperWrapper.style.minHeight = '0';
              swiperWrapper.style.maxHeight = 'none';
            }
            
            slides.forEach(slide => {
              slide.style.height = 'auto';
              slide.style.minHeight = '0';
              slide.style.maxHeight = 'none';
            });
            
            // Update autoHeight to recalculate
            if (swiper.updateAutoHeight) {
              swiper.updateAutoHeight();
            }
            swiper.update();
            swiper.updateSlides();
            swiper.updateSlidesClasses();
          }
        };
        
        // Fix heights immediately
        setTimeout(fixHeights, 50);
        
        // Wait for images and fix again
        const allImages = swiperContainer.querySelectorAll('img');
        let imagesLoaded = 0;
        const totalImages = allImages.length;
        
        if (totalImages === 0) {
          setTimeout(fixHeights, 200);
        } else {
          allImages.forEach(img => {
            if (img.complete) {
              imagesLoaded++;
            } else {
              img.addEventListener('load', fixHeights, { once: true });
              img.addEventListener('error', fixHeights, { once: true });
            }
          });
          
          if (imagesLoaded === totalImages) {
            setTimeout(fixHeights, 200);
          }
          
          setTimeout(fixHeights, 300);
        }
        
        // Defer color swiper initialization to reduce initial load time
        if (swiperWrapper) {
          const initColorSwipers = () => {
            if (window.SuniceColorSwiperInstance) {
              const colorSwiperContainers = swiperWrapper.querySelectorAll('.color-swiper-container:not([data-sunice-color-swiper-initialized])');
              // Use IntersectionObserver for lazy initialization
              colorSwiperContainers.forEach((container) => {
                // Validate container is a valid Element before proceeding
                if (!container || !(container instanceof Element) || !container.isConnected) {
                  return;
                }
                
                const rect = container.getBoundingClientRect();
                const isVisible = rect.top < window.innerHeight + 100 && rect.bottom > -100;
                
                if (isVisible) {
                  try {
                    window.SuniceColorSwiperInstance.initializeColorSwiper(container);
                  } catch (err) {
                    // Silently fail if initialization fails
                  }
                } else {
                  const colorObserver = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                      if (entry.isIntersecting && entry.target instanceof Element && entry.target.isConnected) {
                        try {
                          window.SuniceColorSwiperInstance.initializeColorSwiper(entry.target);
                          colorObserver.unobserve(entry.target);
                        } catch (err) {
                          // Silently fail if initialization fails
                        }
                      }
                    });
                  }, { rootMargin: '100px' });
                  
                  colorObserver.observe(container);
                }
              });
            }
          };
          
          // Defer color swiper initialization
          if (window.requestIdleCallback) {
            requestIdleCallback(initColorSwipers, { timeout: 2000 });
          } else {
            setTimeout(initColorSwipers, 500);
          }
        }
      }).catch(() => {
        // Fallback: initialize immediately if promise fails
        // Only initialize if swiperContainer is still a valid Element
        if (!swiperContainer || !(swiperContainer instanceof Element) || !swiperContainer.isConnected) {
          return;
        }
        
        const globalWindow = /** @type {Record<string, any>} */ (window);
        const SwiperConstructor = globalWindow.Swiper;
        
        if (typeof SwiperConstructor !== 'function') {
          return;
        }
        
        try {
          const swiper = new SwiperConstructor(swiperContainer, swiperConfig);
          this.swiperInstances.set(containerId, swiper);
        } catch (err) {
          // Silently fail if initialization fails
        }
      });
      
      // Return null since swiper is initialized asynchronously
      // The swiper instance is stored in this.swiperInstances
      return null;
    } catch (error) {
      return null;
    }
    };
    
    // Check if slides are already available
    const initialSlideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
    
    if (initialSlideCount > 0) {
      // Slides are already available, initialize immediately
      initializeSwiper();
    } else {
      // Wait for slides to be available (they might be loading asynchronously)
      const waitForSlides = (retries = 50) => {
        const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
        
        if (slideCount === 0 && retries > 0) {
          setTimeout(() => waitForSlides(retries - 1), 100);
          return;
        }
        
        if (slideCount === 0) {
          return;
        }
        
        // Continue with initialization now that slides are available
        initializeSwiper();
      };
      
      // Start waiting for slides
      waitForSlides();
    }
  }

  destroyProductGridSwipers() {
    this.swiperInstances.forEach((swiper, id) => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swiperInstances.clear();
  }
  };

  // Export for use in main swiper file
  window.SuniceProductGridSwiper = SuniceProductGridSwiper;
}
